/**
 * The notified body consultation procedures as table rows for the technical documentation's
 * Part 1 (RA checklist P.8). LOCKSTEP with backend `app/dossier/device_consultations.consultation_rows`
 * (names, order, status words): the PDF and the page show the same table. The basis is stated only
 * here — the TD prompt keeps it out of the classification prose, and the cover names them only.
 */

// Annex IX order; the names the cover line also uses.
const NAMES = {
  clinical_evaluation: 'clinical evaluation (Article 54; Annex IX Section 5.1)',
  medicinal_substance: 'medicinal substance (Section 5.2)',
  human_tissues: 'human tissues or cells (Section 5.3)',
  absorbed_substances: 'absorbed substances (Section 5.4)',
} as const;

type Procedure = keyof typeof NAMES;
const ORDER = Object.keys(NAMES) as Procedure[];

function isProcedure(value: unknown): value is Procedure {
  return typeof value === 'string' && value in NAMES;
}

/** [procedure, "Triggered" | "Not triggered" | "Not determined", basis] for the four, in Annex IX
 *  order — [] when none was answered. Once any is, a procedure left unclear or not reported is
 *  "Not determined" with no basis, never dropped (QA audit 279bf420: the cover read as complete). */
export function consultationRows(entries: unknown): [string, string, string][] {
  if (!Array.isArray(entries)) return [];
  const answered = new Map<Procedure, { applies: boolean; basis: string }>();
  for (const e of entries) {
    if (e === null || typeof e !== 'object') continue;
    const { procedure, applies, basis } = e as Record<string, unknown>;
    if (!isProcedure(procedure) || typeof applies !== 'boolean' || answered.has(procedure)) continue;
    answered.set(procedure, { applies, basis: typeof basis === 'string' ? basis.trim() : '' });
  }
  if (answered.size === 0) return [];
  return ORDER.map((procedure) => {
    const e = answered.get(procedure);
    if (!e) return [NAMES[procedure], 'Not determined', ''];
    return [NAMES[procedure], e.applies ? 'Triggered' : 'Not triggered', e.basis];
  });
}
